// WbStockPage — /weighbridge/stock (Phase C8).
//
// Three tabs over the WB stock surface:
//   Balances  — non-paginated /wb/stock/balances; client-side filter
//   Movements — paginated /wb/stock/movements; server-side filters
//   Alerts    — paginated /wb/stock-alerts + per-row dismiss action;
//               polls every 60s ONLY while this tab is active
//
// Stock is read-only at the server level (no /wb/stock POST endpoint)
// so this page intentionally exposes no "Add" / "Adjust" controls.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Boxes } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { Tabs, type TabItem } from '../../../components/ui/Tabs/Tabs';
import { useToastStore } from '../../../store/toastStore';
import { extractErrorMessage } from '../../../utils/errorMessage';
import { useWbYards } from '../hooks/useWbYards';
import { useWbProducts } from '../hooks/useWbProducts';
import {
  useDismissStockAlert,
  useWbStockAlerts,
  useWbStockBalances,
  useWbStockMovements,
  type BalanceDto,
  type MovementDirection,
  type StockAlertDto,
  type StockAlertStatus,
  type ThresholdStatus,
} from '../hooks/useWbStock';
import styles from './WbStockPage.module.css';

type TabId = 'balances' | 'movements' | 'alerts';

const TAB_ITEMS: TabItem[] = [
  { id: 'balances', label: 'Balances' },
  { id: 'movements', label: 'Movements' },
  { id: 'alerts', label: 'Alerts' },
];

// -- Page -------------------------------------------------------------------

export function WbStockPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabId>('balances');

  return (
    <div className={styles.page}>
      <PageHeader
        title="Stock"
        subtitle="Per-(product, yard) balances, movements, and threshold alerts."
      />

      <Tabs
        items={TAB_ITEMS}
        value={activeTab}
        onChange={(id) => setActiveTab(id as TabId)}
        ariaLabel="Stock tabs"
      />

      {activeTab === 'balances' ? <BalancesTab /> : null}
      {activeTab === 'movements' ? <MovementsTab /> : null}
      {/*
        Keep the Alerts hook mounted only while the tab is active so its
        refetchInterval stops when the user switches away. Unmounting on
        tab change drops the query's polling loop entirely.
      */}
      {activeTab === 'alerts' ? <AlertsTab /> : null}
    </div>
  );
}

// -- Filter bar helpers -----------------------------------------------------

function YardFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const q = useWbYards();
  return (
    <label className={styles.filterField}>
      <span>Yard</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All yards</option>
        {(q.data?.yards ?? []).map((y) => (
          <option key={y.enc_id} value={y.enc_id}>
            {y.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function ProductFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  // Server caps per_page at 100 (see wb/products/types.ts listProductsQuerySchema).
  const q = useWbProducts({ per_page: 100, is_active: true });
  return (
    <label className={styles.filterField}>
      <span>Product</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All products</option>
        {(q.data?.products ?? []).map((p) => (
          <option key={p.enc_id} value={p.enc_id}>
            {p.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// -- Badge helpers ----------------------------------------------------------

function thresholdBadge(status: ThresholdStatus): JSX.Element {
  if (status === 'ok') return <Badge variant="success">OK</Badge>;
  if (status === 'low') return <Badge variant="danger">Low</Badge>;
  if (status === 'high') return <Badge variant="warning">High</Badge>;
  return <Badge variant="muted">Not tracked</Badge>;
}

function directionBadge(direction: MovementDirection): JSX.Element {
  return direction === 'in' ? (
    <Badge variant="success">In</Badge>
  ) : (
    <Badge variant="info">Out</Badge>
  );
}

function alertTypeBadge(type: 'low' | 'high'): JSX.Element {
  return type === 'low' ? (
    <Badge variant="danger">Low stock</Badge>
  ) : (
    <Badge variant="warning">High stock</Badge>
  );
}

function alertStatusBadge(status: StockAlertStatus): JSX.Element {
  if (status === 'active') return <Badge variant="danger">Active</Badge>;
  if (status === 'dismissed') return <Badge variant="muted">Dismissed</Badge>;
  return <Badge variant="info">Auto-resolved</Badge>;
}

// -- Formatters -------------------------------------------------------------

function fmtNumber(n: number): string {
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 }).format(n);
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB');
}

function defaultDateFromIso(): string {
  // 30 days ago.
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// -- Balances tab -----------------------------------------------------------

function thresholdSortKey(s: ThresholdStatus): number {
  // low/high first so urgent rows surface at the top.
  if (s === 'low') return 0;
  if (s === 'high') return 1;
  if (s === 'ok') return 2;
  return 3;
}

function BalancesTab(): JSX.Element {
  const [yardId, setYardId] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const balancesQuery = useWbStockBalances();
  const all = balancesQuery.data?.balances ?? [];

  // Client-side filter + sort — the endpoint is non-paginated, so this
  // is cheap and avoids a refetch as filters change.
  const rows = useMemo(() => {
    let out: BalanceDto[] = all;
    if (yardId) out = out.filter((b) => b.yard.enc_id === yardId);
    if (productId) out = out.filter((b) => b.product.enc_id === productId);
    return [...out].sort((a, b) => {
      const diff =
        thresholdSortKey(a.threshold_status) -
        thresholdSortKey(b.threshold_status);
      if (diff !== 0) return diff;
      return a.product.name.localeCompare(b.product.name);
    });
  }, [all, yardId, productId]);

  return (
    <div className={styles.tab}>
      <div className={styles.filterBar} data-testid="balances-filters">
        <YardFilter value={yardId} onChange={setYardId} />
        <ProductFilter value={productId} onChange={setProductId} />
      </div>

      {balancesQuery.isError ? (
        <p className={styles.error}>
          {extractErrorMessage(balancesQuery.error)}
        </p>
      ) : null}

      <Card flush>
        {balancesQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading balances" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Boxes}
            title="No stock tracked"
            description="Products with stock tracking enabled will appear here."
          />
        ) : (
          <table className={styles.table} data-testid="balances-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Yard</th>
                <th className={styles.numCol}>Balance</th>
                <th>Unit</th>
                <th>Status</th>
                <th className={styles.numCol}>Low threshold</th>
                <th className={styles.numCol}>High threshold</th>
                <th>Last movement</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={`${b.product.enc_id}-${b.yard.enc_id}`}>
                  <td>{b.product.name}</td>
                  <td>{b.yard.name}</td>
                  <td className={styles.numCol}>{fmtNumber(b.balance)}</td>
                  <td>{b.unit_of_measure}</td>
                  <td>{thresholdBadge(b.threshold_status)}</td>
                  <td className={styles.numCol}>
                    {b.stock_threshold_low === null
                      ? '—'
                      : fmtNumber(b.stock_threshold_low)}
                  </td>
                  <td className={styles.numCol}>
                    {b.stock_threshold_high === null
                      ? '—'
                      : fmtNumber(b.stock_threshold_high)}
                  </td>
                  <td>{fmtDateTime(b.last_movement_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// -- Movements tab ----------------------------------------------------------

function MovementsTab(): JSX.Element {
  const [yardId, setYardId] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [direction, setDirection] = useState<'' | MovementDirection>('');
  const [dateFrom, setDateFrom] = useState<string>(defaultDateFromIso());
  const [dateTo, setDateTo] = useState<string>(todayIso());
  const [page, setPage] = useState<number>(1);

  const movementsQuery = useWbStockMovements({
    yard_enc_id: yardId || undefined,
    product_enc_id: productId || undefined,
    direction: direction || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
    per_page: 50,
  });
  const rows = movementsQuery.data?.movements ?? [];
  const pagination = movementsQuery.data?.pagination;

  // Reset to page 1 when any filter changes so the next page doesn't
  // dangle past the filtered result count.
  function withReset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  return (
    <div className={styles.tab}>
      <div className={styles.filterBar}>
        <YardFilter value={yardId} onChange={withReset(setYardId)} />
        <ProductFilter value={productId} onChange={withReset(setProductId)} />
        <label className={styles.filterField}>
          <span>Direction</span>
          <select
            value={direction}
            onChange={(e) =>
              withReset(setDirection)(e.target.value as '' | MovementDirection)
            }
          >
            <option value="">All</option>
            <option value="in">In</option>
            <option value="out">Out</option>
          </select>
        </label>
        <label className={styles.filterField}>
          <span>Date from</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => withReset(setDateFrom)(e.target.value)}
          />
        </label>
        <label className={styles.filterField}>
          <span>Date to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => withReset(setDateTo)(e.target.value)}
          />
        </label>
      </div>

      {movementsQuery.isError ? (
        <p className={styles.error}>
          {extractErrorMessage(movementsQuery.error)}
        </p>
      ) : null}

      <Card flush>
        {movementsQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading movements" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Boxes}
            title="No movements"
            description="No stock movements match the selected filters."
          />
        ) : (
          <table className={styles.table} data-testid="movements-table">
            <thead>
              <tr>
                <th>Date / time</th>
                <th>Product</th>
                <th>Yard</th>
                <th>Direction</th>
                <th className={styles.numCol}>Quantity</th>
                <th className={styles.numCol}>Balance after</th>
                <th>Ticket</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.enc_id}>
                  <td>{fmtDateTime(m.occurred_at)}</td>
                  <td>{m.product.name}</td>
                  <td>{m.yard.name}</td>
                  <td>{directionBadge(m.direction)}</td>
                  <td className={styles.numCol}>
                    {fmtNumber(m.quantity)} {m.unit_of_measure}
                  </td>
                  <td className={styles.numCol}>
                    {fmtNumber(m.balance_after)}
                  </td>
                  <td>
                    {m.ticket ? (
                      <Link
                        to={`/weighbridge/tickets/${m.ticket.enc_id}`}
                        className={styles.ticketLink}
                      >
                        {m.ticket.ticket_number ?? m.ticket.enc_id}
                      </Link>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {pagination && pagination.total > 0 ? (
        <div className={styles.pagination}>
          <span>
            Page {pagination.page} of {pagination.total_pages} ·{' '}
            {pagination.total} record{pagination.total === 1 ? '' : 's'}
          </span>
          <div className={styles.paginationButtons}>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() =>
                setPage((p) => Math.min(pagination.total_pages, p + 1))
              }
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// -- Alerts tab -------------------------------------------------------------

const ALERT_STATUSES: ReadonlyArray<{
  value: StockAlertStatus;
  label: string;
}> = [
  { value: 'active', label: 'Active' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'auto_resolved', label: 'Auto-resolved' },
];

function AlertsTab(): JSX.Element {
  const [statusSet, setStatusSet] = useState<Set<StockAlertStatus>>(
    new Set<StockAlertStatus>(['active']),
  );
  const [yardId, setYardId] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [target, setTarget] = useState<StockAlertDto | null>(null);

  const pushToast = useToastStore((s) => s.push);
  const dismissMut = useDismissStockAlert();

  const statusCsv = [...statusSet].join(',') || 'active';
  const alertsQuery = useWbStockAlerts(
    {
      status: statusCsv,
      yard_enc_id: yardId || undefined,
      product_enc_id: productId || undefined,
      page,
      per_page: 50,
    },
    { enablePolling: true },
  );
  const all = alertsQuery.data?.stock_alerts ?? [];
  const pagination = alertsQuery.data?.pagination;

  // Server-supplied list is the source of truth, but we sort client-side
  // so a non-default ordering doesn't surprise the operator if the
  // server-default later changes.
  const rows = useMemo(
    () =>
      [...all].sort(
        (a, b) =>
          new Date(b.triggered_at).getTime() -
          new Date(a.triggered_at).getTime(),
      ),
    [all],
  );

  function toggleStatus(s: StockAlertStatus) {
    setStatusSet((prev) => {
      const next = new Set(prev);
      if (next.has(s)) {
        next.delete(s);
      } else {
        next.add(s);
      }
      return next;
    });
    setPage(1);
  }

  async function handleDismiss() {
    if (!target) return;
    try {
      await dismissMut.mutateAsync(target.enc_id);
      pushToast({ type: 'success', message: 'Alert dismissed.' });
      setTarget(null);
    } catch (err) {
      pushToast({
        type: 'error',
        message: extractErrorMessage(err) ?? 'Failed to dismiss alert.',
      });
    }
  }

  return (
    <div className={styles.tab}>
      <div className={styles.filterBar}>
        <fieldset className={styles.statusFilter}>
          <legend>Status</legend>
          {ALERT_STATUSES.map((s) => (
            <label key={s.value} className={styles.statusChip}>
              <input
                type="checkbox"
                checked={statusSet.has(s.value)}
                onChange={() => toggleStatus(s.value)}
              />
              {s.label}
            </label>
          ))}
        </fieldset>
        <YardFilter
          value={yardId}
          onChange={(v) => {
            setYardId(v);
            setPage(1);
          }}
        />
        <ProductFilter
          value={productId}
          onChange={(v) => {
            setProductId(v);
            setPage(1);
          }}
        />
      </div>

      {alertsQuery.isError ? (
        <p className={styles.error}>
          {extractErrorMessage(alertsQuery.error)}
        </p>
      ) : null}

      <Card flush>
        {alertsQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading alerts" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={AlertTriangle}
            title="No alerts"
            description="No stock alerts match the selected filters."
          />
        ) : (
          <table className={styles.table} data-testid="alerts-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Yard</th>
                <th>Type</th>
                <th>Triggered</th>
                <th className={styles.numCol}>Current</th>
                <th className={styles.numCol}>Threshold</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.enc_id}>
                  <td>{a.product.name}</td>
                  <td>{a.yard.name}</td>
                  <td>{alertTypeBadge(a.threshold_type)}</td>
                  <td>{fmtDateTime(a.triggered_at)}</td>
                  <td className={styles.numCol}>
                    {fmtNumber(a.current_qty)} {a.product.unit_of_measure}
                  </td>
                  <td className={styles.numCol}>
                    {fmtNumber(a.threshold_qty)} {a.product.unit_of_measure}
                  </td>
                  <td>{alertStatusBadge(a.status)}</td>
                  <td className={styles.actionsCell}>
                    {a.status === 'active' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setTarget(a)}
                      >
                        Dismiss
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {pagination && pagination.total > 0 ? (
        <div className={styles.pagination}>
          <span>
            Page {pagination.page} of {pagination.total_pages} ·{' '}
            {pagination.total} alert{pagination.total === 1 ? '' : 's'}
          </span>
          <div className={styles.paginationButtons}>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() =>
                setPage((p) => Math.min(pagination.total_pages, p + 1))
              }
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={target !== null}
        title="Dismiss alert"
        message={
          target ? (
            <p>
              Dismiss the {target.threshold_type} stock alert for{' '}
              <strong>{target.product.name}</strong> at{' '}
              <strong>{target.yard.name}</strong>?
            </p>
          ) : (
            ''
          )
        }
        confirmLabel="Dismiss"
        onConfirm={handleDismiss}
        onCancel={() => setTarget(null)}
        loading={dismissMut.isPending}
      />
    </div>
  );
}
